/**
 * Notes Aggregator
 *
 * Builds a MonthlyNotesReport from all note records (input notes, output notes,
 * standalone notes) by:
 * 1. Applying severity tags to all notes using classifyNoteSeverity
 * 2. Sorting all tagged notes chronologically by recordedAt
 * 3. Counting by source type: input, output, standalone
 * 4. Collecting attention notes (Clinical Attention or Image Attached)
 * 5. Grouping tagged notes by dateKey
 */

import { NormalizedNoteRecord, MonthlyNotesReport, TaggedNote, SeverityTag } from '../types';
import { classifyNoteSeverity } from '../pipeline/noteTagger';

/**
 * Aggregate all note records into a MonthlyNotesReport.
 *
 * @param allNotes - Array of all normalized note records (input, output, standalone)
 * @param startDate - Start date of the report period (YYYY-MM-DD)
 * @param endDate - End date of the report period (YYYY-MM-DD)
 * @returns A MonthlyNotesReport with tagged notes, counts by source, and attention notes
 */
export function aggregateNotes(
  allNotes: NormalizedNoteRecord[],
  startDate: string,
  endDate: string
): MonthlyNotesReport {
  const patientId = allNotes.length > 0 ? allNotes[0].patientId : '';

  // 1. Apply severity tags to all notes
  const taggedNotes: TaggedNote[] = allNotes.map(note => ({
    note,
    severityTag: classifyNoteSeverity(note),
  }));

  // 2. Sort chronologically by recordedAt
  taggedNotes.sort((a, b) => a.note.recordedAt.localeCompare(b.note.recordedAt));

  // 3. Count by source type
  const inputNotesCount = allNotes.filter(n => n.noteType === 'input').length;
  const outputNotesCount = allNotes.filter(n => n.noteType === 'output').length;
  const standaloneNotesCount = allNotes.filter(n => n.noteType === 'standalone').length;

  // 4. Collect attention notes (Clinical Attention or Image Attached)
  const attentionNotes = taggedNotes.filter(
    tn => tn.severityTag === 'Clinical Attention' || tn.severityTag === 'Image Attached'
  );

  // 5. Group tagged notes by dateKey
  const notesByDate: Record<string, TaggedNote[]> = {};
  for (const taggedNote of taggedNotes) {
    const dateKey = taggedNote.note.dateKey;
    if (!notesByDate[dateKey]) {
      notesByDate[dateKey] = [];
    }
    notesByDate[dateKey].push(taggedNote);
  }

  return {
    patientId,
    startDate,
    endDate,
    totalNotes: allNotes.length,
    inputNotesCount,
    outputNotesCount,
    standaloneNotesCount,
    attentionNotes,
    notesByDate,
  };
}
